import { useState } from 'react';
import styled from '@emotion/styled';
import { Button, DialogContentText, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { OrderJobAsset, PmamImage } from '@/redux/slices/slice_types';
import { getData } from '@/redux/slices/dataTableSlice';
import axios from '@/_utils/axios';
import { Dialog } from '@/components/_common';
import { useAppDispatch } from '@/redux/hooks';
import { useToast } from '@/components/_common/Toast';

const headers = [
  {
    label: 'ID',
    minWidth: 200
  },
  {
    label: 'File',
    minWidth: 350,
    maxWidth: 500
  },
  {
    label: 'Language',
    minWidth: 150
  },
  {
    label: 'Resolution',
    minWidth: 120
  },
  {
    label: '',
    minWidth: 120
  }
];

type PmamImagesProps = {
  assetId: OrderJobAsset['id'];
  images: PmamImage[];
  showApply: boolean;
};

const PmamImages = ({ assetId, images, showApply }: PmamImagesProps) => {
  const { success: successToast, warn: warnToast, error: errorToast } = useToast();
  const dispatch = useAppDispatch();
  const [selectedId, setSelectedId] = useState<PmamImage['pmam_id'] | undefined>();
  const isDialogOpen = Boolean(selectedId);

  const apply = (newSelectedId: PmamImage['pmam_id']) => {
    setSelectedId(newSelectedId);
  };

  const confirmApply = () => {
    const material_id = selectedId;
    setSelectedId(undefined);
    axios
      .post(`/core/jobs/assets/${assetId}/`, { material_id })
      .then((res) => {
        if (res?.data?.success) {
          successToast(`Material ID applied`);
          dispatch(getData());
        } else {
          warnToast(`Material ID not updated`);
        }
      })
      .catch((err) => {
        errorToast(err.message);
        console.error('Failed to apply asset', err);
      });
  };

  return (
    <>
      <StyledTable>
        <TableHead>
          <TableRow>
            {headers.map(({ label, minWidth }, index) => (
              <TableCell key={`${label}-${index}`} sx={{ minWidth }}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {images.map(({ pmam_id, file_name, language, resolution }) => (
            <TableRow key={pmam_id}>
              <TableCell>{pmam_id}</TableCell>
              <TableCell>{file_name}</TableCell>
              <TableCell>{language}</TableCell>
              <TableCell>{resolution}</TableCell>
              <TableCell>
                {showApply && (
                  <Button size="small" variant="outlined" onClick={() => apply(pmam_id)}>
                    Apply
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
      <Dialog title="Do you want to continue?" open={isDialogOpen} onCancel={() => setSelectedId(undefined)} onSubmit={confirmApply}>
        <DialogContentText>Are you sure you want to apply the material id {selectedId} to this Asset?</DialogContentText>
      </Dialog>
    </>
  );
};

export default PmamImages;

const StyledTable = styled(Table)(({ theme }) => ({
  width: 'fit-content',
  marginTop: theme.spacing(1),
  marginLeft: theme.spacing(-2)
}));
